import {
	BigNum,
	LAMPORTS_EXP,
	MarketStatus,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
	ZERO,
	calculateMaxRemainingDeposit,
	isUserBankrupt,
} from '@drift-labs/sdk';
import {
	BaseAssetDisplaySymbol,
	ENUM_UTILS,
	MAIN_POOL_ID,
	UIMarket,
} from '@drift/common';
import { isAmplifyAccountName } from 'src/utils/amplify';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	DEFAULT_ACCOUNT_NAMES_BY_POOL_ID,
	MIN_LEFTOVER_SOL,
	ZERO_QUOTE_BIGNUM,
} from 'src/constants/constants';
import Env from 'src/environmentVariables/EnvironmentVariables';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { useDepositCollateral } from 'src/hooks/driftActions/spot-management/useDepositCollateral';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import useTransferableWalletCollateralBalance from 'src/hooks/useTransferableWalletCollateralBalance';
import { getCloseBorrowAmountWithBuffer } from 'src/utils/borrowLend';
import { toNotional } from 'src/utils/formatting';
import useDriftStore from '../../../stores/DriftStore/useDriftStore';
import { getDepositValidationPrice } from 'src/utils/depositValidation';
import useOraclePrice from 'src/hooks/useOraclePrice';
import { useGetBiggestAssetMarket } from 'src/hooks/useGetBiggestAssetMarket';
import { useAccountCreationCost } from 'src/hooks/useAccountCreationCost';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useCollateralModalState from 'src/components/Modals/CollateralModal/hooks/useCollateralModalState';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';

import NumberDisplay from '../../NumberDisplay/NumberDisplay';
import { useQueryClient } from '@tanstack/react-query';
import { useIsSignedMsgUserOrdersAccountInitialized } from 'src/hooks/signedMsgOrders/useIsSignedMsgUserOrdersAccountInitialized';
import MarketIcon from '../../Utils/MarketIcon';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { cj } from 'src/utils/classNames';
import { CollateralInputV2 } from 'src/components/Inputs/CollateralInput';
import { InputQuickActions } from 'src/components/Inputs/InputQuickActions';
import { useSyncedDepositInput } from 'src/stores/DepositInputStore/useSyncedDepositInput';
import AirdropButton from 'src/components/Buttons/AirdropButton';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useDriftAccountsStore, {
	AccountData,
} from 'src/stores/useDriftAccountsStore';
import Button from 'src/components/primitives/Button/Button';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import ApyBreakdownTooltipV2 from 'src/components/Tooltip/ApyBreakdownTooltipV2';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import { useApyBreakdownTooltipData } from 'src/hooks/useApyBreakdownTooltipData';
import useModal from 'src/components/Modals/hooks/useModal';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import Divider from 'src/components/primitives/Divider/Divider';
import SubAccountSelector from './components/SubAccountSelector';
import BankruptAccountWarning from './BankruptAccountWarning';
import { useCreateNewSubaccount } from 'src/hooks/driftActions/user/useCreateNewSubaccount';
import useIsMainnet from 'src/hooks/useIsMainnet';
import {
	prefetchFunXyzAssets,
	prefetchFunXyzDepositAddress,
} from 'src/hooks/useFunXyzActions';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { DepositFromOtherSourcesContent } from './components/DepositFromOtherSourcesContent';

const MIN_DEPOSIT_AMOUNT_FOR_NEW_ACCT = Env.minInitialDepositValue;

const EarnApy = ({
	selectedMarketIndex,
	marketDisplaySymbol,
}: {
	selectedMarketIndex: number;
	marketDisplaySymbol: string;
}) => {
	const apyData = useApyBreakdownTooltipData(selectedMarketIndex);

	return (
		<div className="flex items-center justify-between">
			<div className="flex items-center gap-1 text-text-secondary">
				<Tooltip
					className="typo-b4"
					content="Deposited assets automatically earn yields."
				>
					Earning APY
				</Tooltip>
			</div>
			<div className="flex items-center gap-2">
				<div className="flex items-center gap-1">
					<MarketIcon
						marketSymbol={marketDisplaySymbol}
						sizeClass="w-4 h-4"
						className="shrink-0"
					/>
					<span className="typo-t4 text-text-default">
						{marketDisplaySymbol}
					</span>
				</div>
				<div className="flex items-center gap-2">
					<ApyBreakdownTooltipV2
						apyChunks={apyData.deposit.chunks}
						showTooltip={apyData.deposit.showTooltip}
					>
						<div className="flex items-center gap-1">
							<NumberDisplay
								value={apyData.deposit.totalApyBN}
								type="percentage"
								className={cj(
									'typo-t4',
									apyData.deposit.totalApy > 0.009
										? 'text-text-positive-green-button'
										: 'text-text-secondary'
								)}
								trimZeroes
							/>
						</div>
					</ApyBreakdownTooltipV2>
				</div>
			</div>
		</div>
	);
};

const BalanceChanges = ({
	selectedCollatBank,
	targetAccountKey,
	selectedTargetAccount,
	collatTransferAmountBigNum,
	assetCurrentPrice,
	baseAssetDisplaySymbol,
}: {
	selectedCollatBank: SpotMarketConfig;
	targetAccountKey: string;
	selectedTargetAccount: AccountData;
	collatTransferAmountBigNum: BigNum;
	assetCurrentPrice: BigNum;
	baseAssetDisplaySymbol: BaseAssetDisplaySymbol;
}) => {
	const targetAccountBankBalance = useAccountTargetSpotBalance(
		selectedCollatBank,
		targetAccountKey
	);

	const targetAccountCurrentNetBaseBalance =
		targetAccountBankBalance.netBaseBalance;

	const targetAccountTotalNetQuoteValue = BigNum.from(
		selectedTargetAccount?.client?.getNetSpotMarketValue() ?? 0,
		QUOTE_PRECISION_EXP
	);

	const quoteAmountToAdd =
		selectedCollatBank?.symbol == 'USDC'
			? collatTransferAmountBigNum.shiftTo(QUOTE_PRECISION_EXP)
			: collatTransferAmountBigNum
					.shiftTo(QUOTE_PRECISION_EXP)
					.mul(assetCurrentPrice)
					.shiftTo(QUOTE_PRECISION_EXP);

	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-center justify-between gap-1">
				<span className="typo-b4 text-text-secondary">New Asset Balance</span>
				<span className="typo-b4 text-text-default">
					{`${targetAccountCurrentNetBaseBalance
						.add(collatTransferAmountBigNum)
						.prettyPrint()} ${baseAssetDisplaySymbol}`}
				</span>
			</div>
			<div className="flex items-center justify-between gap-1">
				<span className="typo-b4 text-text-secondary">
					New Net Account Balance (USD)
				</span>
				<span className="typo-b4 text-text-default">
					{toNotional(
						targetAccountTotalNetQuoteValue.add(quoteAmountToAdd).toNum()
					)}
				</span>
			</div>
		</div>
	);
};

const RentDisclaimers = ({
	accountExists,
	totalAccountCreationCost,
	hasEnoughSolInWallet,
	solRequiredToDeposit,
	isLoadingWalletBalance,
}: {
	accountExists: boolean;
	totalAccountCreationCost: BigNum;
	hasEnoughSolInWallet: boolean;
	solRequiredToDeposit: BigNum;
	isLoadingWalletBalance: boolean;
}) => {
	if (isLoadingWalletBalance) {
		return null;
	}

	return (
		<>
			{!accountExists && hasEnoughSolInWallet && (
				<span className="typo-b4 text-text-secondary">
					Reclaimable account setup fee:{' '}
					{totalAccountCreationCost.prettyPrint(true, 1)} SOL
				</span>
			)}
			{!hasEnoughSolInWallet && (
				<InlineAlert variant="error" title="Not enough SOL in your wallet">
					{solRequiredToDeposit.prettyPrint(true, 4)} SOL is needed{' '}
					{accountExists
						? 'to cover for future transaction gas fees'
						: 'to cover a new sub-account creation fees (refundable after closing the account) and gas fees'}
				</InlineAlert>
			)}
		</>
	);
};

const DepositModal = ({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) => {
	const borrowData = useDriftStore((s) => s.borrowLendData);
	const { depositCollateralForExistingAccount } = useDepositCollateral();
	const {
		selectedTargetAccountKey,
		selectedTargetAccount,
		selectedPoolId,
		selectedCollateralMarket,
		handleTargetAccountChange,
		defaultToLargestBalance,
	} = useCollateralModalState({
		collateralIndex,
		collateralPoolId,
		targetAccountKey,
		defaultLargestBalance,
	});

	const accountExists = !!selectedTargetAccount;
	const solBalance = useDriftStore((s) => s.wallet.currentSolBalance);
	const userStatsAccount = useDriftStore((s) => s.userStatsAccount);
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();
	const { captureEvent } = usePostHogCapture();
	const isWalletConnected = useWalletIsConnected();
	const queryClient = useQueryClient();
	const { data: isSignedMsgUserOrdersAccountInitialized } =
		useIsSignedMsgUserOrdersAccountInitialized();
	const { hide, show } = useModal();
	const createNewSubaccount = useCreateNewSubaccount();
	const isMainnet = useIsMainnet();
	const isDepositFromOtherSourcesEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_DEPOSIT_FROM_OTHER_SOURCES')
	);
	const hasMainPoolAccount = useDriftAccountsStore((s) =>
		Object.values(s.accounts).some(
			(acc) => acc !== undefined && acc.poolId === MAIN_POOL_ID
		)
	);

	const getBiggestAssetMarket = useGetBiggestAssetMarket();
	const biggestAssetMarket = getBiggestAssetMarket(selectedPoolId);

	const { totalCost: totalAccountCreationCost } = useAccountCreationCost();

	const [submitting, setSubmitting] = useState(false);

	const {
		selectedCollatBank,
		depositInputAmount,
		targetAccountKeyInput,
		persistInputAmountRef,
		setSelectedMarket,
		setCollatTransferAmount,
		setPersistInputAmount,
	} = useSyncedDepositInput(
		defaultToLargestBalance,
		biggestAssetMarket,
		selectedCollateralMarket.marketIndex,
		selectedTargetAccountKey
	);

	const filterOutMarketIndices = useMemo(() => {
		return borrowData
			.filter((bank) =>
				ENUM_UTILS.match(
					bank.spotMarketAccount.status,
					MarketStatus.REDUCE_ONLY
				)
			)
			.map((bank) => bank.bankIndex);
	}, [borrowData]);

	const selectedBankUiConfig = UIMarket.createSpotMarket(
		selectedCollatBank.marketIndex
	);

	const [walletDepositableBalanceBigNum, loadingWalletDepositableBalance] =
		useTransferableWalletCollateralBalance(selectedCollatBank, !accountExists);

	const uiMarket = UIMarket.createSpotMarket(selectedCollatBank?.marketIndex);
	const spotMarketAccount =
		driftClientIsReady && !!selectedCollatBank && driftClient
			? driftClient.getSpotMarketAccount(selectedCollatBank?.marketIndex)
			: undefined;

	// Get target account balance to check for borrow positions
	const targetAccountBankBalance = useAccountTargetSpotBalance(
		selectedCollatBank,
		selectedTargetAccountKey
	);

	// Calculate buffered close borrow amount if there's a borrow position
	const bufferedCloseBorrowAmount = useMemo(() => {
		if (!driftClient || !selectedCollatBank || !targetAccountBankBalance) {
			return BigNum.zero(
				selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP
			);
		}

		// Check if there's a borrow position (negative net balance)
		const hasBorrowPosition = targetAccountBankBalance.netBaseBalance.isNeg();

		// If there's a borrow position, ensure we can deposit at least the borrow amount
		if (hasBorrowPosition) {
			return getCloseBorrowAmountWithBuffer(
				driftClient,
				selectedCollatBank.marketIndex,
				targetAccountBankBalance.netBaseBalance
			);
		}

		return BigNum.zero(selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP);
	}, [driftClient, selectedCollatBank, targetAccountBankBalance]);

	const remainingDepositsAllowed = useMemo(() => {
		if (!spotMarketAccount) {
			return BigNum.zero(
				selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP
			);
		}

		const maxRemainingDeposit = BigNum.from(
			calculateMaxRemainingDeposit(spotMarketAccount),
			selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP
		);

		if (bufferedCloseBorrowAmount.gt(BigNum.zero())) {
			return BigNum.max(maxRemainingDeposit, bufferedCloseBorrowAmount);
		}

		return maxRemainingDeposit;
	}, [
		selectedCollatBank?.precisionExp,
		spotMarketAccount,
		bufferedCloseBorrowAmount,
	]);

	const spotMarketMaxTokenDeposits =
		spotMarketAccount?.maxTokenDeposits || ZERO;
	const maxAllowableDepositBigNum = spotMarketMaxTokenDeposits.eq(ZERO)
		? walletDepositableBalanceBigNum
		: BigNum.max(
				BigNum.zero(remainingDepositsAllowed.precision),
				BigNum.min(remainingDepositsAllowed, walletDepositableBalanceBigNum)
			);

	const collatTransferAmountBigNum = useMemo(
		() =>
			BigNum.fromPrint(
				depositInputAmount ? depositInputAmount : '0',
				selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP
			),
		[depositInputAmount, selectedCollatBank?.precisionExp]
	);

	const oraclePrice = useOraclePrice(uiMarket.marketId) ?? ZERO_QUOTE_BIGNUM;

	const exceededMax =
		collatTransferAmountBigNum.gt(maxAllowableDepositBigNum) &&
		!loadingWalletDepositableBalance;

	const validationPrice = getDepositValidationPrice(
		uiMarket.marketIndex,
		oraclePrice
	);

	const exceedsMinDeposit =
		collatTransferAmountBigNum
			.shiftTo(QUOTE_PRECISION_EXP)
			.mul(validationPrice)
			.toNum() >= MIN_DEPOSIT_AMOUNT_FOR_NEW_ACCT;

	const isBelowMinDeposit = !exceedsMinDeposit && !userStatsAccount; // only require minimum deposit for new users

	const solRequiredToDeposit = (
		accountExists ? BigNum.zero(LAMPORTS_EXP) : totalAccountCreationCost
	).add(MIN_LEFTOVER_SOL);
	const hasEnoughSolInWallet = solBalance.gte(solRequiredToDeposit);

	const isEmptyAmplifyAccount =
		accountExists &&
		isAmplifyAccountName(selectedTargetAccount.name) &&
		!selectedTargetAccount.spotBalances.some((balance) =>
			balance.balance.gtZero()
		);

	const allowConfirmation =
		isWalletConnected &&
		!exceededMax &&
		maxAllowableDepositBigNum.gtZero() &&
		!submitting &&
		depositInputAmount &&
		collatTransferAmountBigNum.gt(
			BigNum.zero(collatTransferAmountBigNum.precision)
		) &&
		!isBelowMinDeposit &&
		hasEnoughSolInWallet &&
		!isEmptyAmplifyAccount;

	const handleDeposit = useCallback(async () => {
		setSubmitting(true);

		let success: boolean | undefined;
		try {
			const collateralAmount = BigNum.fromPrint(
				depositInputAmount,
				selectedCollatBank?.precisionExp ?? QUOTE_PRECISION_EXP
			);

			if (accountExists && selectedTargetAccount.poolId === selectedPoolId) {
				captureEvent('submitted_deposit', {
					spot_market_symbol: selectedCollatBank?.symbol,
					newAccount: false,
					depositAmount: collateralAmount.toNum(),
				});

				success = await depositCollateralForExistingAccount(
					collateralAmount,
					selectedCollatBank,
					targetAccountKeyInput,
					false,
					!isSignedMsgUserOrdersAccountInitialized
				);

				if (success && queryClient) {
					queryClient.invalidateQueries({
						queryKey: [
							'isSignedMsgUserOrdersAccountInitialized',
							selectedTargetAccount?.authority?.toBase58(),
						],
					});
				}
			} else {
				captureEvent('submitted_deposit', {
					spot_market_symbol: selectedCollatBank?.symbol,
					newAccount: true,
					depositAmount: collateralAmount.toNum(),
				});

				success = await createNewSubaccount({
					depositAmount: collateralAmount,
					spotMarketIndex: selectedCollatBank.marketIndex,
					poolId: selectedPoolId,
				});
			}
		} catch {
			success = false;
		}

		if (success) {
			hide();
		} else {
			setSubmitting(false);
		}
	}, [
		accountExists,
		captureEvent,
		depositInputAmount,
		hide,
		queryClient,
		selectedCollatBank,
		selectedPoolId,
		selectedTargetAccount,
		targetAccountKey,
	]);

	const handleValueChange = (newValue?: string) => {
		// Set input to max amount if exceeds limit
		if (newValue && newValue !== '') {
			// Use selectedCollatBank.precisionExp to match maxAllowableDepositBigNum precision
			const inputBigNum = BigNum.fromPrint(
				newValue,
				selectedCollatBank.precisionExp
			);

			if (inputBigNum.lte(maxAllowableDepositBigNum)) {
				setCollatTransferAmount(newValue);
			} else {
				// Set to max amount if input exceeds limit
				setCollatTransferAmount(maxAllowableDepositBigNum.print());
			}
		} else {
			setCollatTransferAmount(newValue ?? '');
		}
	};

	// Prefetch Fun.xyz assets and deposit address for faster loading when opening the other sources modal (only when flag enabled)
	useEffect(() => {
		if (!isDepositFromOtherSourcesEnabled) return;

		prefetchFunXyzAssets().catch(() => {
			// Silently fail - will retry when modal opens
		});

		// Also prefetch deposit address if wallet is connected
		if (selectedTargetAccount?.authority) {
			const recipientAddress = selectedTargetAccount.authority.toBase58();
			const subAccountId = selectedTargetAccount.userId ?? 0;
			prefetchFunXyzDepositAddress(recipientAddress, subAccountId).catch(() => {
				// Silently fail - will retry when modal opens
			});
		}
	}, [
		isDepositFromOtherSourcesEnabled,
		selectedTargetAccount?.authority,
		selectedTargetAccount?.userId,
	]);

	// If the user is not connected, show the connect wallet modal first,
	// then return to the deposit modal.
	useEffect(() => {
		if (isWalletConnected) {
			return;
		}

		const returnToDepositModal = () => {
			show({
				type: 'deposit',
				props: {
					collateralIndex: collateralIndex ?? 0,
					targetAccountKey: targetAccountKey ?? '',
					defaultLargestBalance: defaultToLargestBalance,
				},
			});
		};

		show({
			type: 'connect_wallet',
			props: {
				callback: returnToDepositModal,
			},
		});
	}, [
		isWalletConnected,
		selectedCollatBank,
		selectedPoolId,
		targetAccountKey,
		defaultToLargestBalance,
		show,
		collateralIndex,
	]);

	// If the user has exceeded the max allowable deposit, set the deposit input amount to the max allowable deposit
	useEffect(() => {
		if (exceededMax && isWalletConnected) {
			setCollatTransferAmount(maxAllowableDepositBigNum.print());
		}
	}, [exceededMax, isWalletConnected]);

	// reset the deposit input amount when market index changes, unless persistInputAmountRef is true
	useEffect(() => {
		if (persistInputAmountRef.current) {
			setPersistInputAmount(false);
			return;
		}
		setCollatTransferAmount('');
	}, [selectedCollatBank?.marketIndex, selectedPoolId]);

	const isMobile = useIsMobileScreenSize();

	const isAccountBankrupt =
		selectedTargetAccount?.client &&
		isUserBankrupt(selectedTargetAccount.client);

	if (isAccountBankrupt) {
		return (
			<BaseModal.Container
				onClose={hide}
				open={true}
				dataTestId="deposit-modal-bankrupt-warning"
			>
				<BaseModal.Title
					onClose={hide}
					closeButtonTestId="deposit-modal-bankrupt-warning-close"
				></BaseModal.Title>
				<BaseModal.Body>
					<BankruptAccountWarning />
				</BaseModal.Body>
			</BaseModal.Container>
		);
	}

	const handleDepositFromOtherSources = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		show({
			type: 'deposit_from_other_sources',
			props: {
				collateralIndex,
				collateralPoolId,
				targetAccountKey: selectedTargetAccountKey,
				defaultLargestBalance,
				returnToDepositModal: true,
			},
		});
	};

	const showDesktopDepositFromOtherSources =
		!isMobile &&
		isDepositFromOtherSourcesEnabled &&
		accountExists &&
		hasMainPoolAccount;

	return (
		<BaseModal.Container
			onClose={hide}
			open={true}
			dataTestId="deposit-modal"
			containerClassName="flex-col"
			className={
				showDesktopDepositFromOtherSources
					? 'gap-y-xs overflow-visible bg-transparent shadow-none'
					: undefined
			}
		>
			<div
				className={cj(
					'flex h-full w-full flex-col overflow-hidden bg-container-bg text-text-default shadow-md sm:h-auto sm:max-h-[85vh] sm:w-min sm:max-w-[632px] sm:min-w-[416px] sm:rounded-sm',
					showDesktopDepositFromOtherSources && 'sm:max-h-[calc(85vh-72px)]'
				)}
			>
				<BaseModal.Title onClose={hide} closeButtonTestId="deposit-modal-close">
					Deposit Funds
				</BaseModal.Title>
				<BaseModal.Body className="min-h-0">
					{!isMainnet && <AirdropButton spotMarket={selectedCollatBank} />}

					<SubAccountSelector
						mode="deposit"
						newSubaccount={{
							isNewAccount: !accountExists,
							defaultName: DEFAULT_ACCOUNT_NAMES_BY_POOL_ID[selectedPoolId],
						}}
						selectedTargetAccountKey={selectedTargetAccountKey}
						selectedPoolId={selectedPoolId}
						handleTargetAccountChange={handleTargetAccountChange}
						includeDelegates
					/>

					{/** Token/Size Input */}
					<CollateralInputV2
						maxAmount={maxAllowableDepositBigNum}
						selectedMarket={selectedCollatBank}
						onChangeValue={handleValueChange}
						onChangeMarket={setSelectedMarket}
						value={depositInputAmount}
						label="Amount"
						defaultToHighestBalance={defaultToLargestBalance}
						source={{
							type: 'wallet',
							filterOutMarketIndices,
						}}
						customElementRight={
							<InputQuickActions.SizePresets
								onClick={(amount) => setCollatTransferAmount(amount.print())}
								maxAmount={maxAllowableDepositBigNum}
								isLoading={loadingWalletDepositableBalance}
							/>
						}
						poolId={selectedPoolId}
						searchBodyWithApy={true}
						fullScreenPopover={isMobile}
						fullScreenPopoverTitle="Select Asset"
					/>

					<EarnApy
						selectedMarketIndex={selectedCollatBank?.marketIndex}
						marketDisplaySymbol={
							selectedBankUiConfig.uiSymbols.marketDisplaySymbol
						}
					/>

					<Divider className="bg-stroke-divider" />

					<BalanceChanges
						selectedCollatBank={selectedCollatBank}
						targetAccountKey={targetAccountKeyInput}
						selectedTargetAccount={selectedTargetAccount as AccountData}
						collatTransferAmountBigNum={collatTransferAmountBigNum}
						assetCurrentPrice={oraclePrice}
						baseAssetDisplaySymbol={
							selectedBankUiConfig.uiSymbols.baseAssetDisplaySymbol
						}
					/>
				</BaseModal.Body>
				<BaseModal.Footer className="flex-col items-stretch">
					<Button
						className="w-full"
						size="lg"
						onClick={handleDeposit}
						disabled={!allowConfirmation}
						isLoading={submitting}
						dataTestId="deposit-modal-submit"
					>
						{isBelowMinDeposit
							? `Minimum Initial Deposit: $${MIN_DEPOSIT_AMOUNT_FOR_NEW_ACCT}`
							: 'Deposit'}
					</Button>
					{isEmptyAmplifyAccount && (
						<InlineAlert
							variant="warning"
							title="Depositing into an empty Amplify account is not allowed."
						/>
					)}
					<RentDisclaimers
						accountExists={accountExists}
						totalAccountCreationCost={totalAccountCreationCost}
						hasEnoughSolInWallet={hasEnoughSolInWallet}
						solRequiredToDeposit={solRequiredToDeposit}
						isLoadingWalletBalance={loadingWalletDepositableBalance}
					/>
				</BaseModal.Footer>

				{/* Mobile: Deposit from other sources inside modal */}
				{isMobile &&
					isDepositFromOtherSourcesEnabled &&
					accountExists &&
					hasMainPoolAccount && (
						<div className="border-t border-stroke-divider p-4">
							<button
								onClick={handleDepositFromOtherSources}
								className="pointer-events-auto flex h-[68px] w-full cursor-pointer items-center gap-sm rounded-sm bg-button-secondary-bg px-4 text-text-default transition-colors hover:bg-button-secondary-bg-hover"
							>
								<DepositFromOtherSourcesContent />
							</button>
						</div>
					)}
			</div>

			{/* Desktop: Deposit from other sources below modal */}
			{showDesktopDepositFromOtherSources && (
				<button
					onClick={handleDepositFromOtherSources}
					className="pointer-events-auto flex h-[68px] w-full shrink-0 cursor-pointer items-center gap-sm rounded-sm bg-container-bg px-4 text-text-default shadow-md transition-colors hover:bg-input-bg sm:max-w-[632px] sm:min-w-[416px]"
				>
					<DepositFromOtherSourcesContent />
				</button>
			)}
		</BaseModal.Container>
	);
};

export default React.memo(DepositModal);
